<template>
  <main class="bg-bg-3 min-h-dvh">
    <AppHeader v-if="showHeader" :shareable-link-logo="shareableLinkLogo" />

    <section
      v-if="isUnavailable"
      class="mx-auto max-w-[560px] px-[16px] py-[64px] text-center"
    >
      <h1 class="text-gpx_lg text-gpx_black-500 mb-[8px] font-semibold">
        This tracking link is unavailable
      </h1>
      <p class="text-gpx_sm text-gpx_gray-200">
        The link may have been turned off, or it may be incorrect. Check with
        whoever shared it with you.
      </p>
    </section>

    <section v-else-if="isPending" class="px-[16px] py-[64px] text-center">
      <p class="text-gpx_sm text-gpx_gray-200">Loading tracker…</p>
    </section>

    <div
      v-else
      class="sm:flex"
      :class="showHeader ? 'sm:h-layout-tablet md:h-layout-desktop' : 'sm:h-[100dvh]'"
    >
      <aside
        class="border-gpx_gray-900 w-full overflow-y-auto bg-white px-[16px] py-[24px] sm:h-full sm:w-4/12 sm:border-r sm:px-[24px]"
      >
        <div class="mb-[16px] flex items-center gap-x-[8px]">
          <h1 class="text-gpx_lg text-gpx_black-500 font-semibold">
            {{ trackerName }}
          </h1>
          <span
            class="bg-gpx_red-100/10 text-gpx_xs text-gpx_red-100 flex items-center gap-x-[5px] rounded-full px-[8px] py-[2px] font-medium"
          >
            <span class="bg-gpx_red-100 size-[6px] rounded-full"></span>
            Live
          </span>
        </div>
        <p class="text-gpx_xs text-gpx_gray-200 font-medium uppercase">
          Last known location
        </p>
        <p class="text-gpx_md text-gpx_black-500 font-semibold">
          {{ currentAddress }}
        </p>
        <p class="text-gpx_sm text-gpx_gray-200 mb-[16px]">
          {{ lastReported }}
        </p>
        <VDatePickerRange
          v-model="dateModel"
          :initialValue="dateModel"
          :maxDate="true"
          :show-tools="false"
          teleport
          unlimited
          utc
          tooltip="Select date range"
        />
      </aside>

      <div class="h-[360px] w-full sm:h-full sm:w-8/12">
        <ReportMap
          :positionsHistory="positions"
          :currentPosition="currentPosition"
          simple-popups
        />
      </div>
    </div>
  </main>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { endOfDay, startOfDay } from 'date-fns';
import { getPublicTracker } from '@gpx-spa/queries';
import AppHeader from '@/components/_common/AppHeader.vue';
import ReportMap from '@/components/report/map/ReportMap.vue';
import { formatTime } from '@/filters';
import { setTitle } from '@/utils/title';

const route = useRoute();

const uuid = computed(() => route.params.uuid);

const dateModel = ref([
  startOfDay(new Date()).toISOString(),
  endOfDay(new Date()).toISOString(),
]);
const params = computed(() => ({
  date_from: dateModel.value[0],
  date_to: dateModel.value[1],
}));

const { data: tracker, isPending, isError } = getPublicTracker(uuid, params);

// A disabled link and an unknown uuid are the same failure by design — never
// tell the viewer which one this is.
const isUnavailable = computed(
  () => isError.value || (!isPending.value && !tracker.value),
);

const trackerName = computed(() => tracker.value?.name ?? 'Tracker');
const positions = computed(() => tracker.value?.positions ?? []);
const currentPosition = computed(() => tracker.value?.current_position ?? null);
const shareableLinkLogo = computed(
  () => tracker.value?.shareable_link_logo ?? null,
);
const showHeader = computed(() => shareableLinkLogo.value?.mode !== 'none');

const formatPositionDate = (timestamp) =>
  timestamp ? formatTime(timestamp, 'E, MMM do, yyyy h:mmaaa') : '-';

const currentAddress = computed(
  () => currentPosition.value?.address || 'No location reported yet',
);
const lastReported = computed(() =>
  currentPosition.value?.timestamp
    ? `Last reported ${formatPositionDate(currentPosition.value.timestamp)}`
    : '',
);

watch(
  [trackerName, isUnavailable],
  ([name, unavailable]) => {
    setTitle(unavailable ? 'Tracker unavailable' : name);
  },
  { immediate: true },
);
</script>
